import { useState, useEffect, useRef, useCallback } from 'react';
import Hls from 'hls.js';
import { Camera, Play, AlertCircle, Film, Loader2,
         Trash2, Edit2, Plus, X, ChevronLeft, Maximize2 } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import { muze } from '../lib/moduly';
import TabBar from '../components/layout/TabBar';
import ZaznamyTimeline, { timeToSeconds } from '../components/ZaznamyTimeline';
import ZpetTlacitko from '../components/ui/ZpetTlacitko';

const SECTION_COLOR = '#EF4444';
const SECTION_BG = 'rgba(239,68,68,0.12)';

// ── HLS přehrávač ────────────────────────────────────────────────────────────

function KameraPlayer({ hlsUrl, autoPlay = true, controls = false, style = {}, onStatus }) {
    const videoRef   = useRef(null);
    const hlsRef     = useRef(null);
    const retryRef   = useRef(null);
    const statusRef  = useRef('active');
    const [ready,  setReady]  = useState(false);
    const [chyba,  setChyba]  = useState(false);

    const onStatusRef = useRef(onStatus);
    onStatusRef.current = onStatus;

    const setChybaS = useCallback((v) => { setChyba(v); onStatusRef.current?.(v ? 'error' : 'ok'); }, []);
    const setReadyS = useCallback((v) => {
        setReady(v);
        if (v) { setChyba(false); onStatusRef.current?.('ok'); }
    }, []);

    const initHls = useCallback((isRetry = false) => {
        const video = videoRef.current;
        if (!video || statusRef.current !== 'active') return;

        hlsRef.current?.destroy();
        hlsRef.current = null;
        clearTimeout(retryRef.current);
        video.removeAttribute('src');
        video.load();
        if (!isRetry) setChybaS(false);

        if (!hlsUrl) { setChybaS(true); return; }

        if (Hls.isSupported()) {
            const hls = new Hls({
                startPosition: -1,
                liveSyncDurationCount: 1,
                liveMaxLatencyDurationCount: 4,
                maxBufferLength: 8,
                manifestLoadingMaxRetry: 0,
                levelLoadingMaxRetry: 4,
            });
            hlsRef.current = hls;
            hls.loadSource(hlsUrl);
            hls.attachMedia(video);
            hls.on(Hls.Events.MANIFEST_PARSED, () => {
                if (autoPlay) video.play().catch(() => {});
            });

            let stallCount = 0;
            hls.on(Hls.Events.ERROR, (_e, data) => {
                if (!data.fatal && (
                    data.details === Hls.ErrorDetails.BUFFER_STALLED_ERROR ||
                    data.details === Hls.ErrorDetails.BUFFER_SEEK_OVER_HOLE
                )) {
                    if (++stallCount >= 2) {
                        hls.destroy(); hlsRef.current = null; setReadyS(false);
                        retryRef.current = setTimeout(() => { if (statusRef.current === 'active') initHls(true); }, 2000);
                    }
                    return;
                }
                if (data.fatal) {
                    hls.destroy(); hlsRef.current = null; setReadyS(false);
                    setChybaS(true);
                    retryRef.current = setTimeout(() => { if (statusRef.current === 'active') initHls(true); }, 6000);
                }
            });
        } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
            video.src = hlsUrl;
            if (autoPlay) video.play().catch(() => {});
        } else {
            setChybaS(true);
        }
    }, [hlsUrl, autoPlay, setChybaS, setReadyS]);

    useEffect(() => {
        const video = videoRef.current;
        if (!video) return;
        const onReady = () => setReadyS(true);
        video.addEventListener('playing', onReady);
        video.addEventListener('canplay', onReady);
        return () => { video.removeEventListener('playing', onReady); video.removeEventListener('canplay', onReady); };
    }, [setReadyS]);

    useEffect(() => {
        statusRef.current = 'active';
        initHls();
        return () => {
            statusRef.current = 'inactive';
            hlsRef.current?.destroy();
            hlsRef.current = null;
            clearTimeout(retryRef.current);
        };
    }, [initHls]);

    return (
        <div style={{ position: 'relative', width: '100%', background: '#000', borderRadius: 'inherit', overflow: 'hidden', ...style }}>
            <video ref={videoRef} muted autoPlay playsInline controls={controls}
                style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block', opacity: ready && !chyba ? 1 : 0 }} />
            {(!ready || chyba) && (
                <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column',
                    alignItems: 'center', justifyContent: 'center', gap: 8, background: '#0a0a0f' }}>
                    {chyba
                        ? <><AlertCircle size={28} color={SECTION_COLOR} /><span style={{ color: 'rgba(255,255,255,0.4)', fontSize: 12 }}>Offline</span></>
                        : <><Loader2 size={24} className="animate-spin" style={{ color: 'rgba(255,255,255,0.3)' }} /><span style={{ color: 'rgba(255,255,255,0.3)', fontSize: 12 }}>Načítám…</span></>
                    }
                </div>
            )}
        </div>
    );
}

// ── Hlavní stránka ───────────────────────────────────────────────────────────

export default function Kamery({ user }) {
    const { theme } = useTheme();
    const isDark = theme === 'dark' || (theme === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches);

    // Záložky = podstránky modulu (přístupy kamery.zive / zaznamy / sprava)
    const povoleneTaby = ['zive', 'zaznamy', 'sprava'].filter(id => muze(user, `kamery.${id}`));

    const mutedColor = isDark ? 'rgba(255,255,255,0.45)' : 'var(--text-muted)';
    const secondaryColor = isDark ? 'rgba(255,255,255,0.55)' : 'var(--text-secondary)';

    const glassCard = isDark ? {
        background: 'rgba(255,255,255,0.03)',
        border: '1px solid rgba(255,255,255,0.06)',
        backdropFilter: 'blur(32px)', WebkitBackdropFilter: 'blur(32px)',
        boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.04)',
    } : {
        background: 'var(--surface-raised)',
        border: '1px solid var(--border-default)',
        boxShadow: 'var(--shadow-xs)',
    };

    const inputStyle = {
        background: isDark ? 'rgba(255,255,255,0.06)' : 'var(--surface-base)',
        border: `1px solid ${isDark ? 'rgba(255,255,255,0.12)' : 'var(--border-default)'}`,
        color: 'var(--text-primary)',
        borderRadius: 6, padding: '8px 12px', fontSize: 13, width: '100%',
        outline: 'none', boxSizing: 'border-box',
        colorScheme: isDark ? 'dark' : 'light',
    };

    const [tab, setTab]       = useState(() => povoleneTaby[0] ?? 'zive');
    const [kamery, setKamery] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.innerWidth < 768);
    useEffect(() => {
        const onResize = () => setIsMobile(window.innerWidth < 768);
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, []);

    // Záznamy
    const [vybKameraId, setVybKameraId] = useState(null);
    const [vybDatum, setVybDatum]       = useState(new Date().toISOString().slice(0, 10));
    const [zaznamy, setZaznamy]         = useState([]);
    const [nactZaznamy, setNactZaznamy] = useState(false);
    const [currentIndex, setCurrentIndex] = useState(-1);
    const [playbackTime, setPlaybackTime] = useState(-1);
    const videoRef = useRef(null);

    // Správa — form
    const [form, setForm]             = useState(null);
    const [editId, setEditId]         = useState(null);
    const [saving, setSaving]         = useState(false);
    const [formErr, setFormErr]       = useState({});

    // Živě — fullscreen + stav streamu per kamera
    const [fullscreenId, setFullscreenId] = useState(null);
    const [streamStavy, setStreamStavy]   = useState({});

    const handleStreamStatus = useCallback((id, status) => {
        setStreamStavy(prev => prev[id] === status ? prev : { ...prev, [id]: status });
    }, []);

    const csrf = () => document.querySelector('meta[name="csrf-token"]')?.content;

    // Načtení kamer
    useEffect(() => {
        fetch('/api/kamery')
            .then(r => r.json())
            .then(data => { setKamery(Array.isArray(data) ? data : []); setLoading(false); })
            .catch(() => setLoading(false));
    }, []);

    // Načtení záznamů
    useEffect(() => {
        if (tab !== 'zaznamy' || !vybKameraId) return;
        setNactZaznamy(true);
        setZaznamy([]);
        setCurrentIndex(-1);
        setPlaybackTime(-1);
        fetch(`/api/kamery/${vybKameraId}/zaznamy?datum=${vybDatum}`)
            .then(r => r.json()).then(data => {
                // Seřadit chronologicky (API vrací desc)
                const sorted = (Array.isArray(data) ? data : []).sort((a, b) =>
                    (a.datum_cas || '').localeCompare(b.datum_cas || ''));
                setZaznamy(sorted);
                setNactZaznamy(false);
            });
    }, [tab, vybKameraId, vybDatum]);

    // Playlist helpers
    const playlist = zaznamy; // již chronologicky seřazené

    const playFile = useCallback((index, seekOffset = 0) => {
        setCurrentIndex(index);
        const video = videoRef.current;
        if (!video || !playlist[index]) return;
        video.src = playlist[index].url;
        video.load();
        const onReady = () => {
            if (seekOffset > 0) video.currentTime = seekOffset;
            video.play().catch(() => {});
            video.removeEventListener('loadedmetadata', onReady);
        };
        video.addEventListener('loadedmetadata', onReady);
    }, [playlist]);

    const handleTimelineSeek = useCallback((absoluteSeconds) => {
        // Najít soubor, který pokrývá daný čas
        const idx = playlist.findIndex(z => {
            if (!z.datum_cas || !z.trvani_s) return false;
            const start = timeToSeconds(z.datum_cas);
            return absoluteSeconds >= start && absoluteSeconds < start + z.trvani_s;
        });

        if (idx >= 0) {
            const start = timeToSeconds(playlist[idx].datum_cas);
            playFile(idx, absoluteSeconds - start);
        } else {
            // Mezera — najít nejbližší následující soubor
            const nextIdx = playlist.findIndex(z => {
                if (!z.datum_cas) return false;
                return timeToSeconds(z.datum_cas) > absoluteSeconds;
            });
            if (nextIdx >= 0) playFile(nextIdx, 0);
        }
    }, [playlist, playFile]);

    const handleVideoEnded = useCallback(() => {
        if (currentIndex < playlist.length - 1) {
            playFile(currentIndex + 1, 0);
        }
    }, [currentIndex, playlist, playFile]);

    const handleTimeUpdate = useCallback(() => {
        const video = videoRef.current;
        if (!video || currentIndex < 0 || !playlist[currentIndex]) return;
        const fileStart = timeToSeconds(playlist[currentIndex].datum_cas);
        setPlaybackTime(fileStart + video.currentTime);
    }, [currentIndex, playlist]);

    // ── Správa CRUD ──

    function openForm(kamera = null) {
        setEditId(kamera?.id ?? null);
        setFormErr({});
        setForm(kamera ? { ...kamera } : { kod: '', nazev: '', lokace: '', razeni: 99, aktivni: true, poznamka: '', uchovavat_dni: '' });
    }

    async function submitForm(e) {
        e.preventDefault();
        setSaving(true); setFormErr({});
        const url    = editId ? `/api/kamery/${editId}` : '/api/kamery';
        const method = editId ? 'PUT' : 'POST';
        try {
            const res = await fetch(url, {
                method,
                headers: { 'Content-Type': 'application/json', 'Accept': 'application/json', 'X-CSRF-TOKEN': csrf() },
                body: JSON.stringify(form),
            });
            const data = await res.json();
            if (!res.ok) { setFormErr(data.errors ?? {}); setSaving(false); return; }
            setKamery(prev => editId ? prev.map(k => k.id === data.id ? data : k) : [...prev, data]);
            setForm(null);
        } catch {
            setFormErr({ _: ['Chyba komunikace se serverem.'] });
        }
        setSaving(false);
    }

    async function deleteKamera(id) {
        if (!confirm('Smazat kameru?')) return;
        await fetch(`/api/kamery/${id}`, { method: 'DELETE', headers: { 'X-CSRF-TOKEN': csrf() } });
        setKamery(prev => prev.filter(k => k.id !== id));
    }

    // ── Computed ──
    const aktivniKamery = kamery.filter(k => k.aktivni);

    // ── Render ──
    return (
        <div>
            {/* Header — na mobilu se zobrazuje v TopBaru, tady skryjeme */}
            {!isMobile && (
                <div className="flex items-center gap-3 mb-5">
                    <div className="flex items-center justify-center shrink-0"
                        style={{ width: 40, height: 40, borderRadius: 9, background: SECTION_BG }}>
                        <Camera size={20} style={{ color: SECTION_COLOR }} />
                    </div>
                    <div className="min-w-0 flex-1">
                        <h1 className="text-lg font-bold" style={{ color: 'var(--text-primary)' }}>Kamerový systém</h1>
                        <p className="text-xs" style={{ color: secondaryColor }}>
                            {loading ? 'Načítám…' : `${aktivniKamery.length} aktivních kamer`}
                        </p>
                    </div>
                </div>
            )}

            {/* Záložky z katalogu — na mobilu je přepíná střed lišty. */}
            <div style={{ marginBottom: isMobile ? 0 : 20 }}>
                <TabBar modul="kamery" user={user} aktivni={tab} onZmena={setTab}
                    barva={SECTION_COLOR} isDark={isDark} isMobile={isMobile} />
            </div>

            {/* ── Živě ─────────────────────────────────────────────────────── */}
            {tab === 'zive' && (
                <>
                    {fullscreenId && (() => {
                        const k = aktivniKamery.find(c => c.id === fullscreenId);
                        return (
                            <div style={{ position: 'fixed', inset: 0, background: '#000', zIndex: 1000,
                                display: 'flex', flexDirection: 'column' }}>
                                <div style={{
                                    display: 'flex', alignItems: 'center', gap: 10,
                                    paddingTop: 'max(12px, env(safe-area-inset-top))',
                                    paddingBottom: 12,
                                    paddingLeft: 'max(16px, env(safe-area-inset-left))',
                                    paddingRight: 'max(16px, env(safe-area-inset-right))',
                                    background: 'rgba(0,0,0,0.8)',
                                }}>
                                    <ZpetTlacitko onClick={() => setFullscreenId(null)} popis="Zpět na kamery" barva="#fff" style={{ marginLeft: -6 }} />
                                    <span style={{ color: '#fff', fontWeight: 600, fontSize: 14 }}>{k?.nazev}</span>
                                    {k?.lokace && <span style={{ color: 'rgba(255,255,255,0.5)', fontSize: 13 }}>{k.lokace}</span>}
                                </div>
                                <div style={{ flex: 1, paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}>
                                    {k && <KameraPlayer hlsUrl={k.hls_url} controls style={{ height: '100%' }} />}
                                </div>
                            </div>
                        );
                    })()}

                    {loading ? (
                        <div className="flex items-center justify-center" style={{ minHeight: 200, color: mutedColor }}>
                            <Loader2 size={24} className="animate-spin" />
                        </div>
                    ) : aktivniKamery.length === 0 ? (
                        <div className="rounded-2xl p-12 text-center" style={glassCard}>
                            <Camera size={36} style={{ color: mutedColor, margin: '0 auto 12px' }} />
                            <p className="text-sm" style={{ color: secondaryColor, margin: 0 }}>
                                Žádné aktivní kamery. Přidejte kamery v záložce Správa.
                            </p>
                        </div>
                    ) : (
                        <div style={{ display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: 16 }}>
                            {aktivniKamery.map(k => (
                                <div key={k.id} className="rounded-2xl overflow-hidden" style={glassCard}>
                                    <div style={{ position: 'relative', aspectRatio: '16/9', cursor: 'pointer' }}
                                        onClick={() => setFullscreenId(k.id)}>
                                        <KameraPlayer hlsUrl={k.hls_url} style={{ height: '100%' }}
                                            onStatus={s => handleStreamStatus(k.id, s)} />
                                        <div style={{ position: 'absolute', top: 8, right: 8,
                                            background: 'rgba(0,0,0,0.5)', borderRadius: 6, padding: '5px 7px',
                                            backdropFilter: 'blur(8px)' }}>
                                            <Maximize2 size={14} color="#fff" />
                                        </div>
                                    </div>
                                    <div style={{ padding: '12px 16px', display: 'flex', alignItems: 'center',
                                        justifyContent: 'space-between' }}>
                                        <div>
                                            <div className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>
                                                {k.nazev}
                                            </div>
                                            {k.lokace && (
                                                <div className="text-xs" style={{ color: mutedColor, marginTop: 2 }}>
                                                    {k.lokace}
                                                </div>
                                            )}
                                        </div>
                                        {streamStavy[k.id] === 'error' ? (
                                            <span className="flex items-center gap-1.5 text-[11px]" style={{ color: mutedColor }}>
                                                <span style={{ width: 6, height: 6, borderRadius: '50%',
                                                    background: mutedColor, display: 'inline-block' }} />
                                                Offline
                                            </span>
                                        ) : (
                                            <span className="flex items-center gap-1.5 text-[11px]" style={{ color: '#10B981' }}>
                                                <span style={{ width: 6, height: 6, borderRadius: '50%',
                                                    background: '#10B981', display: 'inline-block' }} />
                                                Živě
                                            </span>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </>
            )}

            {/* ── Záznamy ──────────────────────────────────────────────────── */}
            {tab === 'zaznamy' && (
                <div>
                    {/* Filtry */}
                    <div className="rounded-2xl mb-4 flex gap-3 flex-wrap items-center" style={{ ...glassCard, padding: 16 }}>
                        <select value={vybKameraId ?? ''} onChange={e => setVybKameraId(e.target.value || null)}
                            style={{ ...inputStyle, flex: 1, minWidth: 160, width: 'auto' }}>
                            <option value="">— Vyberte kameru —</option>
                            {aktivniKamery.map(k => (
                                <option key={k.id} value={k.id}>{k.nazev}{k.lokace ? ` (${k.lokace})` : ''}</option>
                            ))}
                        </select>
                        <input type="date" value={vybDatum} onChange={e => setVybDatum(e.target.value)}
                            style={{ ...inputStyle, width: 'auto' }} />
                        <div className="flex-1" />
                    </div>

                    {!vybKameraId ? (
                        <div className="rounded-2xl p-12 text-center" style={glassCard}>
                            <Film size={36} style={{ color: mutedColor, margin: '0 auto 12px' }} />
                            <p className="text-sm" style={{ color: secondaryColor, margin: 0 }}>Vyberte kameru a datum</p>
                        </div>
                    ) : nactZaznamy ? (
                        <div className="flex items-center justify-center py-12">
                            <Loader2 size={24} className="animate-spin" style={{ color: mutedColor }} />
                        </div>
                    ) : zaznamy.length === 0 ? (
                        <div className="rounded-2xl p-12 text-center" style={glassCard}>
                            <Film size={36} style={{ color: mutedColor, margin: '0 auto 12px' }} />
                            <p className="text-sm" style={{ color: secondaryColor, margin: 0 }}>Žádné záznamy pro vybrané datum.</p>
                        </div>
                    ) : (
                        <>
                            {/* Video přehrávač */}
                            <div className="rounded-2xl overflow-hidden mb-4" style={glassCard}>
                                {currentIndex >= 0 && playlist[currentIndex] ? (
                                    <>
                                        <div className="flex items-center justify-between" style={{
                                            padding: '10px 16px',
                                            borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : 'var(--border-default)'}`,
                                        }}>
                                            <span className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>
                                                {playlist[currentIndex].datum_cas ?? playlist[currentIndex].soubor}
                                            </span>
                                            <div className="flex items-center gap-2">
                                                <button onClick={() => { setCurrentIndex(-1); setPlaybackTime(-1); }}
                                                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: mutedColor, padding: 4 }}>
                                                    <X size={16} />
                                                </button>
                                            </div>
                                        </div>
                                        <video ref={videoRef} controls
                                            onEnded={handleVideoEnded}
                                            onTimeUpdate={handleTimeUpdate}
                                            style={{ width: '100%', display: 'block', maxHeight: 450, background: '#000' }} />
                                    </>
                                ) : (
                                    <div className="p-8 text-center text-sm" style={{ color: secondaryColor }}>
                                        Klikněte na časovou osu nebo na záznam v seznamu.
                                    </div>
                                )}
                            </div>

                            {/* Timeline bar */}
                            <div className="rounded-2xl mb-4" style={{ ...glassCard, padding: '16px 16px 8px' }}>
                                <ZaznamyTimeline
                                    zaznamy={playlist}
                                    onSeek={handleTimelineSeek}
                                    currentTime={playbackTime}
                                    sectionColor={SECTION_COLOR}
                                    isDark={isDark}
                                />
                            </div>

                            {/* Seznam souborů */}
                            <div className="rounded-2xl overflow-hidden" style={glassCard}>
                                <div className="flex items-center gap-2 px-4 py-2.5" style={{
                                    borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : 'var(--border-default)'}`,
                                }}>
                                    <Film size={14} style={{ color: mutedColor }} />
                                    <span className="text-xs font-semibold" style={{ color: mutedColor }}>
                                        {playlist.length} {playlist.length === 1 ? 'záznam' : playlist.length < 5 ? 'záznamy' : 'záznamů'}
                                    </span>
                                </div>
                                <div style={{ maxHeight: 240, overflowY: 'auto' }}>
                                    {playlist.map((z, i) => (
                                        <div key={z.soubor} onClick={() => playFile(i, 0)}
                                            className="flex items-center gap-3 transition-colors"
                                            style={{
                                                padding: '8px 16px', cursor: 'pointer',
                                                borderBottom: i < playlist.length - 1 ? `1px solid ${isDark ? 'rgba(255,255,255,0.04)' : 'var(--border-default)'}` : 'none',
                                                background: currentIndex === i ? `${SECTION_COLOR}12` : 'transparent',
                                            }}>
                                            <Play size={12} style={{ color: currentIndex === i ? SECTION_COLOR : mutedColor, flexShrink: 0 }} />
                                            <span className="flex-1 text-xs" style={{ color: 'var(--text-primary)' }}>
                                                {z.datum_cas ? z.datum_cas.slice(11, 19) : z.soubor}
                                                {z.trvani_s && (
                                                    <span style={{ color: mutedColor, marginLeft: 6 }}>
                                                        ({Math.floor(z.trvani_s / 60)} min)
                                                    </span>
                                                )}
                                            </span>
                                            <span className="text-[11px]" style={{ color: mutedColor }}>
                                                {z.velikost_mb ? `${z.velikost_mb} MB` : '—'}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </>
                    )}
                </div>
            )}

            {/* ── Správa ───────────────────────────────────────────────────── */}
            {tab === 'sprava' && povoleneTaby.includes('sprava') && (
                <>
                    <div className="flex justify-end mb-4">
                        <button onClick={() => openForm()}
                            className="flex items-center gap-1.5"
                            style={{ padding: '8px 16px', borderRadius: 6, fontSize: 13, fontWeight: 600,
                                background: SECTION_COLOR, color: '#fff', border: 'none', cursor: 'pointer' }}>
                            <Plus size={14} /> Přidat kameru
                        </button>
                    </div>

                    <div className="rounded-2xl overflow-hidden" style={glassCard}>
                        {kamery.length === 0 ? (
                            <div className="p-12 text-center">
                                <Camera size={36} style={{ color: mutedColor, margin: '0 auto 12px' }} />
                                <p className="text-sm" style={{ color: secondaryColor, margin: 0 }}>Zatím žádné kamery.</p>
                            </div>
                        ) : (
                            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                <thead>
                                    <tr style={{ borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : 'var(--border-default)'}` }}>
                                        {['Název', 'Kód', 'Lokace', 'Řazení', 'Uchování', 'Stav', ''].map(h => (
                                            <th key={h} className="text-xs font-semibold" style={{
                                                padding: '12px 16px', textAlign: 'left', color: mutedColor,
                                            }}>{h}</th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {kamery.map(k => (
                                        <tr key={k.id} style={{ borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : 'var(--border-default)'}` }}>
                                            <td style={{ padding: '12px 16px' }}>
                                                <span className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>{k.nazev}</span>
                                            </td>
                                            <td style={{ padding: '12px 16px' }}>
                                                <span className="text-xs font-mono" style={{ color: mutedColor }}>{k.kod}</span>
                                            </td>
                                            <td className="text-sm" style={{ padding: '12px 16px', color: secondaryColor }}>
                                                {k.lokace || '—'}
                                            </td>
                                            <td className="text-sm" style={{ padding: '12px 16px', color: secondaryColor }}>
                                                {k.razeni}
                                            </td>
                                            <td className="text-xs" style={{ padding: '12px 16px', color: secondaryColor }}>
                                                {k.uchovavat_dni === 0 ? '∞ nemazat' : k.uchovavat_dni ? `${k.uchovavat_dni} dní` : '7 d (výchozí)'}
                                            </td>
                                            <td style={{ padding: '12px 16px' }}>
                                                <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full"
                                                    style={{
                                                        background: k.aktivni ? 'rgba(16,185,129,0.10)' : 'rgba(100,100,100,0.10)',
                                                        color: k.aktivni ? '#10B981' : mutedColor,
                                                        border: `1px solid ${k.aktivni ? 'rgba(16,185,129,0.25)' : 'rgba(100,100,100,0.2)'}`,
                                                    }}>
                                                    {k.aktivni ? 'Aktivní' : 'Neaktivní'}
                                                </span>
                                            </td>
                                            <td style={{ padding: '12px 16px' }}>
                                                <div className="flex items-center gap-2">
                                                    <button onClick={() => openForm(k)}
                                                        style={{ width: 30, height: 30, borderRadius: 6, border: 'none',
                                                            background: 'none', color: secondaryColor, cursor: 'pointer',
                                                            display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                        <Edit2 size={14} />
                                                    </button>
                                                    <button onClick={() => deleteKamera(k.id)}
                                                        style={{ width: 30, height: 30, borderRadius: 6, border: 'none',
                                                            background: 'none', color: '#EF4444', cursor: 'pointer',
                                                            display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                        <Trash2 size={14} />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </div>

                    {/* Form modal */}
                    {form && (
                        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[9999] flex items-center justify-center p-4"
                            onMouseDown={e => e.target === e.currentTarget && setForm(null)}>
                            <div className="rounded-2xl w-full max-w-md overflow-hidden" style={{
                                background: isDark ? 'rgba(15,15,20,0.95)' : 'var(--surface-raised)',
                                border: `1px solid ${isDark ? 'rgba(255,255,255,0.10)' : 'var(--border-default)'}`,
                                boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
                                backdropFilter: 'blur(32px)', WebkitBackdropFilter: 'blur(32px)',
                            }}>
                                <div className="flex items-center justify-between px-5 py-3.5" style={{
                                    borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'var(--border-default)'}`,
                                }}>
                                    <h3 className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>
                                        {editId ? 'Upravit kameru' : 'Přidat kameru'}
                                    </h3>
                                    <button onClick={() => setForm(null)}
                                        style={{ color: mutedColor, background: 'none', border: 'none', cursor: 'pointer' }}>
                                        <X size={16} />
                                    </button>
                                </div>
                                <form onSubmit={submitForm} className="p-5 space-y-3">
                                    {[
                                        { key: 'nazev',   label: 'Název *',    placeholder: 'Vstup hlavní' },
                                        { key: 'kod',     label: 'Kód (MediaMTX stream) *', placeholder: 'vstup-hlavni' },
                                        { key: 'lokace',  label: 'Lokace',     placeholder: 'Přízemí, vstupní dveře' },
                                        { key: 'razeni',  label: 'Pořadí',     placeholder: '1', type: 'number' },
                                        { key: 'poznamka',label: 'Poznámka',   placeholder: '', textarea: true },
                                        { key: 'uchovavat_dni', label: 'Uchování záznamů (dny)', placeholder: '7 (výchozí)', type: 'number' },
                                    ].map(({ key, label, placeholder, type, textarea }) => (
                                        <div key={key}>
                                            <label className="block text-xs font-semibold mb-1" style={{ color: secondaryColor }}>{label}</label>
                                            {textarea ? (
                                                <textarea value={form[key] ?? ''} onChange={e => setForm(p => ({ ...p, [key]: e.target.value }))}
                                                    rows={2} placeholder={placeholder}
                                                    style={{ ...inputStyle, resize: 'vertical',
                                                        borderColor: formErr[key] ? '#ef4444' : undefined }} />
                                            ) : (
                                                <input type={type || 'text'} value={form[key] ?? ''} placeholder={placeholder}
                                                    onChange={e => setForm(p => ({ ...p, [key]: type === 'number' ? +e.target.value : e.target.value }))}
                                                    style={{ ...inputStyle,
                                                        borderColor: formErr[key] ? '#ef4444' : undefined }} />
                                            )}
                                            {formErr[key] && <span className="text-xs" style={{ color: '#ef4444' }}>{formErr[key][0]}</span>}
                                            {key === 'uchovavat_dni' && (
                                                <span className="block text-[11px] mt-1" style={{ color: mutedColor }}>
                                                    0 = záznamy nemazat · prázdné = výchozí 7 dní
                                                </span>
                                            )}
                                        </div>
                                    ))}
                                    <label className="flex items-center gap-2 cursor-pointer select-none" style={{ color: secondaryColor }}>
                                        <input type="checkbox" checked={form.aktivni}
                                            onChange={e => setForm(p => ({ ...p, aktivni: e.target.checked }))}
                                            style={{ accentColor: SECTION_COLOR, width: 16, height: 16, cursor: 'pointer' }} />
                                        <span className="text-sm">Aktivní</span>
                                    </label>
                                    <div className="flex gap-3 justify-end pt-2">
                                        <button type="button" onClick={() => setForm(null)} disabled={saving}
                                            style={{
                                                padding: '8px 16px', borderRadius: 6, fontSize: 13, fontWeight: 500,
                                                background: isDark ? 'rgba(255,255,255,0.08)' : 'var(--surface-base)',
                                                color: secondaryColor,
                                                border: `1px solid ${isDark ? 'rgba(255,255,255,0.12)' : 'var(--border-default)'}`,
                                                cursor: 'pointer',
                                            }}>
                                            Zrušit
                                        </button>
                                        <button type="submit" disabled={saving}
                                            style={{
                                                padding: '8px 16px', borderRadius: 6, fontSize: 13, fontWeight: 600,
                                                background: SECTION_COLOR, color: '#fff', border: 'none',
                                                cursor: saving ? 'not-allowed' : 'pointer',
                                                opacity: saving ? 0.6 : 1,
                                                display: 'flex', alignItems: 'center', gap: 6,
                                            }}>
                                            {saving && <Loader2 size={14} className="animate-spin" />}
                                            {saving ? 'Ukládám…' : (editId ? 'Uložit' : 'Přidat')}
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    )}
                </>
            )}
        </div>
    );
}
